package br.com.vvrefeicoes.portal.feature.forms

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.Checkbox
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilterChip
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.zIndex
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.di.rememberSessionPermissions
import br.com.vvrefeicoes.portal.core.ui.components.AppBanner
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppFooter
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.AppToast
import br.com.vvrefeicoes.portal.core.ui.components.MultiSelectField
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.components.SearchableSelect
import br.com.vvrefeicoes.portal.core.ui.components.SelectOption
import br.com.vvrefeicoes.portal.core.ui.layout.AppScrollColumn
import br.com.vvrefeicoes.portal.core.ui.layout.appLayoutMetrics
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.domain.model.PermissionAction
import br.com.vvrefeicoes.portal.domain.model.PermissionKeys
import br.com.vvrefeicoes.portal.domain.model.allows
import br.com.vvrefeicoes.portal.feature.notifications.RichBody
import br.com.vvrefeicoes.portal.feature.notifications.RichTextToolbar
import com.composables.icons.lucide.ArrowLeft
import com.composables.icons.lucide.Check
import com.composables.icons.lucide.Eye
import com.composables.icons.lucide.GripVertical
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Pencil
import com.composables.icons.lucide.Plus
import com.composables.icons.lucide.Trash2
import com.mohamedrejeb.richeditor.model.rememberRichTextState
import com.mohamedrejeb.richeditor.ui.material3.RichTextEditor
import com.mohamedrejeb.richeditor.ui.material3.RichTextEditorDefaults
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.StringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.forms_draft_saved
import kotlin.math.roundToInt

@Composable
fun FormBuilderScreen(
    formId: String?,
    readOnly: Boolean,
    onBack: () -> Unit,
    onSaved: () -> Unit,
    onSubmitted: () -> Unit,
) {
    val appGraph = LocalAppGraph.current
    val layout = appLayoutMetrics()
    val permissions = rememberSessionPermissions()
    val session by appGraph.authRepository.session.collectAsStateWithLifecycle()
    val currentUserName = session?.displayName ?: "Usuário"
    val scope = rememberCoroutineScope()
    val allowedSectorLabels = remember(permissions) { allowedFormSectors(permissions).map { it.label } }
    val canManage =
        allowedSectorLabels.isNotEmpty() &&
            (
                if (formId == null) {
                    permissions.allows(PermissionKeys.FORMULARIOS, PermissionAction.CREATE)
                } else {
                    permissions.allows(PermissionKeys.FORMULARIOS, PermissionAction.EDIT)
                }
            )
    val effectiveReadOnly = readOnly
    val controller =
        remember(appGraph.formsRepository, scope, formId, effectiveReadOnly, currentUserName) {
            FormBuilderController(
                repository = appGraph.formsRepository,
                scope = scope,
                formId = formId,
                readOnly = effectiveReadOnly || !canManage,
                currentUserName = currentUserName,
            )
        }
    var toast by remember { mutableStateOf<StringResource?>(null) }
    var previewVisible by remember { mutableStateOf(false) }

    LaunchedEffect(controller) { controller.init() }
    LaunchedEffect(toast) {
        if (toast != null) {
            delay(2200)
            toast = null
        }
    }

    Box(modifier = Modifier.fillMaxSize()) {
        AppScrollColumn(
            modifier = Modifier.fillMaxSize(),
            contentPadding = layout.pagePadding,
            verticalArrangement = Arrangement.spacedBy(layout.pageGap),
            footer = { AppFooter() },
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                Text(
                    text = if (controller.isEditing) "Editar formulário" else "Novo formulário",
                    style = MaterialTheme.typography.headlineMedium,
                )
                AppButton(
                    text = "Voltar",
                    onClick = onBack,
                    variant = PortalButtonVariant.Text,
                    leadingIcon = Lucide.ArrowLeft,
                )
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = "Criado por ${controller.createdBy} · ${formUpdatedAtLabel(controller.updatedAt)}",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                if (!effectiveReadOnly && canManage) {
                    Row(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
                        AppButton(
                            text = "Pré-visualizar",
                            onClick = { previewVisible = true },
                            enabled = !controller.loading,
                            variant = PortalButtonVariant.Text,
                            leadingIcon = Lucide.Eye,
                        )
                        AppButton(
                            text = "Salvar rascunho",
                            onClick = {
                                controller.saveDraft {
                                    toast = Res.string.forms_draft_saved
                                    onSaved()
                                }
                            },
                            enabled = !controller.saving,
                            variant = PortalButtonVariant.Secondary,
                            leadingIcon = Lucide.Pencil,
                        )
                        AppButton(
                            text = "Enviar para aprovação",
                            onClick = { controller.submitForApproval { onSubmitted() } },
                            enabled = !controller.saving,
                            variant = PortalButtonVariant.Filled,
                            leadingIcon = Lucide.Check,
                        )
                    }
                }
            }

            controller.errorMessage?.let { message ->
                AppBanner(text = message, onDismiss = controller::dismissError, isError = true)
            }
            controller.rejectionReason?.let { reason ->
                AppBanner(
                    text = "Formulário rejeitado: $reason",
                    onDismiss = controller::dismissRejectionReason,
                    isError = true,
                )
            }

            if (controller.loading) {
                Box(modifier = Modifier.fillMaxWidth().padding(AppSpacing.space48), contentAlignment = Alignment.Center) {
                    androidx.compose.material3.CircularProgressIndicator()
                }
            } else {
                FormHeaderCard(
                    controller = controller,
                    editable = !effectiveReadOnly && canManage,
                    sectorOptions = (allowedSectorLabels + controller.sectors).distinct(),
                )
                controller.sections.forEach { section ->
                    key(section.id) {
                        FormSectionCard(
                            section = section,
                            editable = !effectiveReadOnly && canManage,
                            controller = controller,
                        )
                    }
                }
                if (!effectiveReadOnly && canManage) {
                    AppButton(
                        text = "Adicionar seção",
                        onClick = controller::addSection,
                        variant = PortalButtonVariant.Text,
                        leadingIcon = Lucide.Plus,
                    )
                }
                FormSignaturesCard(
                    controller = controller,
                    editable = !effectiveReadOnly && canManage,
                )
            }
        }
        toast?.let { message ->
            AppToast(
                message = message,
                modifier = Modifier.align(Alignment.BottomCenter).padding(bottom = AppSpacing.space24),
            )
        }
        if (previewVisible) {
            AppModalCard(
                title = controller.title.ifBlank { "Pré-visualização" },
                onDismiss = { previewVisible = false },
                wide = true,
            ) {
                FormReadContent(
                    description = controller.description,
                    sectors = controller.sectors,
                    createdBy = controller.createdBy,
                    approvedBy = null,
                    updatedAt = controller.updatedAt,
                    sections = controller.sections,
                    signatures = controller.signatures,
                )
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun FormSignaturesCard(
    controller: FormBuilderController,
    editable: Boolean,
) {
    val layout = appLayoutMetrics()
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(layout.cardPadding),
            verticalArrangement = Arrangement.spacedBy(layout.cardGap),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = "Assinaturas",
                        style = MaterialTheme.typography.titleMedium,
                    )
                    Text(
                        text = "As assinaturas aparecem ao final do formulário.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
                if (editable) {
                    AppButton(
                        text = "Adicionar assinatura",
                        onClick = controller::addSignature,
                        variant = PortalButtonVariant.Text,
                        leadingIcon = Lucide.Plus,
                    )
                }
            }
            if (controller.signatures.isEmpty()) {
                Text(
                    text = "Nenhuma assinatura adicionada.",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            } else {
                controller.signatures.forEach { signature ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
                    ) {
                        AppTextField(
                            value = signature.label,
                            onValueChange = { controller.updateSignatureLabel(signature.id, it) },
                            placeholder = "Ex.: Diretor de SGI",
                            enabled = editable,
                            modifier = Modifier.weight(1f),
                        )
                        if (editable) {
                            IconButton(onClick = { controller.removeSignature(signature.id) }) {
                                Icon(
                                    imageVector = Lucide.Trash2,
                                    contentDescription = "Remover assinatura",
                                    tint = MaterialTheme.colorScheme.error,
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FormHeaderCard(
    controller: FormBuilderController,
    editable: Boolean,
    sectorOptions: List<String>,
) {
    val layout = appLayoutMetrics()
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(layout.cardPadding),
            verticalArrangement = Arrangement.spacedBy(layout.cardGap),
        ) {
            AppTextField(
                value = controller.title,
                onValueChange = controller::updateTitle,
                placeholder = "Digite o título do formulário",
                enabled = editable,
                modifier = Modifier.fillMaxWidth(),
            )
            AppTextField(
                value = controller.description,
                onValueChange = controller::updateDescription,
                placeholder = "Descreva o objetivo do formulário",
                enabled = editable,
                singleLine = false,
                modifier = Modifier.fillMaxWidth(),
            )
            Text(
                text = "${controller.description.length}/350 caracteres",
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Text(
                text = "Setores responsáveis",
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            if (editable) {
                MultiSelectField(
                    options = sectorOptions.map { SelectOption(it, it) },
                    selectedIds = controller.sectors.toSet(),
                    onToggle = { sector ->
                        controller.updateSectors(
                            if (sector in controller.sectors) {
                                controller.sectors.toSet() - sector
                            } else {
                                controller.sectors.toSet() + sector
                            },
                        )
                    },
                    searchPlaceholder = "Buscar setor",
                    emptyLabel = "Nenhum setor encontrado.",
                )
            } else {
                FlowRow(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
                    controller.sectors.forEach { sector ->
                        FilterChip(selected = true, onClick = {}, label = { Text(sector) })
                    }
                }
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun FormSectionCard(
    section: FormSection,
    editable: Boolean,
    controller: FormBuilderController,
) {
    var addMenu by remember(section.id) { mutableStateOf(false) }
    var draggedItemId by remember(section.id) { mutableStateOf<String?>(null) }
    var draggedItemOffset by remember(section.id) { mutableStateOf(0f) }
    val index = controller.sections.indexOfFirst { it.id == section.id } + 1
    val layout = appLayoutMetrics()

    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(layout.cardPadding),
            verticalArrangement = Arrangement.spacedBy(layout.cardGap),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
            ) {
                Surface(
                    modifier = Modifier.size(32.dp),
                    shape = AppShapes.large,
                    color = MaterialTheme.colorScheme.primary,
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Text(
                            text = index.toString(),
                            style = MaterialTheme.typography.labelLarge,
                            color = MaterialTheme.colorScheme.onPrimary,
                        )
                    }
                }
                AppTextField(
                    value = section.title,
                    onValueChange = { controller.updateSectionTitle(section.id, it) },
                    placeholder = "Nome da seção",
                    enabled = editable,
                    modifier = Modifier.weight(1f),
                )
                if (editable) {
                    Box {
                        AppButton(
                            text = "Adicionar",
                            onClick = { addMenu = true },
                            variant = PortalButtonVariant.Text,
                            leadingIcon = Lucide.Plus,
                        )
                        DropdownMenu(expanded = addMenu, onDismissRequest = { addMenu = false }) {
                            DropdownMenuItem(
                                text = { Text("Pergunta") },
                                onClick = {
                                    controller.addQuestion(section.id)
                                    addMenu = false
                                },
                            )
                            DropdownMenuItem(
                                text = { Text("Bloco de texto") },
                                onClick = {
                                    controller.addTextItem(section.id)
                                    addMenu = false
                                },
                            )
                            DropdownMenuItem(
                                text = { Text("Tabela de perguntas") },
                                onClick = {
                                    controller.addQuestionTable(section.id)
                                    addMenu = false
                                },
                            )
                            DropdownMenuItem(
                                text = { Text("Tabela de conteúdo") },
                                onClick = {
                                    controller.addTextTable(section.id)
                                    addMenu = false
                                },
                            )
                            DropdownMenuItem(
                                text = { Text("Tabela de pontuação") },
                                onClick = {
                                    controller.addCalculationTable(section.id)
                                    addMenu = false
                                },
                            )
                            DropdownMenuItem(
                                text = { Text("Matriz de perguntas") },
                                onClick = {
                                    controller.addQuestionMatrix(section.id)
                                    addMenu = false
                                },
                            )
                        }
                    }
                }
                if (editable) {
                    IconButton(onClick = { controller.removeSection(section.id) }) {
                        Icon(
                            imageVector = Lucide.Trash2,
                            contentDescription = "Remover seção",
                            tint = MaterialTheme.colorScheme.error,
                        )
                    }
                }
            }

            if (section.items.isEmpty()) {
                Text(
                    text = "Adicione uma pergunta, bloco de texto ou uma tabela.",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            } else {
                section.items.forEach { item ->
                    key(item.id) {
                        val isDragging = draggedItemId == item.id
                        val dragHandle: (@Composable () -> Unit)? =
                            if (editable) {
                                {
                                    FormItemDragHandle(
                                        modifier =
                                            Modifier.pointerInput(item.id) {
                                                var accumulated = 0f
                                                val threshold = 56.dp.toPx()
                                                detectDragGestures(
                                                    onDragStart = {
                                                        draggedItemId = item.id
                                                        draggedItemOffset = 0f
                                                    },
                                                    onDragEnd = {
                                                        draggedItemId = null
                                                        draggedItemOffset = 0f
                                                    },
                                                    onDragCancel = {
                                                        draggedItemId = null
                                                        draggedItemOffset = 0f
                                                    },
                                                    onDrag = { change, dragAmount ->
                                                        change.consume()
                                                        draggedItemOffset += dragAmount.y
                                                        accumulated += dragAmount.y
                                                        while (accumulated >= threshold) {
                                                            controller.moveItem(section.id, item.id, 1)
                                                            accumulated -= threshold
                                                        }
                                                        while (accumulated <= -threshold) {
                                                            controller.moveItem(section.id, item.id, -1)
                                                            accumulated += threshold
                                                        }
                                                    },
                                                )
                                            },
                                    )
                                }
                            } else {
                                null
                            }
                        Box(
                            modifier =
                                Modifier
                                    .fillMaxWidth()
                                    .zIndex(if (isDragging) 1f else 0f)
                                    .offset {
                                        IntOffset(
                                            x = 0,
                                            y = if (isDragging) draggedItemOffset.roundToInt() else 0,
                                        )
                                    },
                        ) {
                            when (item.type) {
                                FormItemType.TEXT ->
                                    FormTextItem(
                                        sectionId = section.id,
                                        item = item,
                                        editable = editable,
                                        controller = controller,
                                        dragHandle = dragHandle,
                                    )
                                FormItemType.QUESTION ->
                                    FormQuestionItem(
                                        sectionId = section.id,
                                        item = item,
                                        editable = editable,
                                        controller = controller,
                                        dragHandle = dragHandle,
                                    )
                                FormItemType.CALCULATION_TABLE ->
                                    FormChoiceTableItem(
                                        sectionId = section.id,
                                        item = item,
                                        editable = editable,
                                        showWeights = true,
                                        controller = controller,
                                        dragHandle = dragHandle,
                                    )
                                FormItemType.QUESTION_TABLE ->
                                    FormQuestionTableItem(
                                        sectionId = section.id,
                                        item = item,
                                        editable = editable,
                                        controller = controller,
                                        dragHandle = dragHandle,
                                    )
                                FormItemType.QUESTION_MATRIX ->
                                    FormQuestionMatrixItem(
                                        sectionId = section.id,
                                        item = item,
                                        editable = editable,
                                        controller = controller,
                                        dragHandle = dragHandle,
                                    )
                                FormItemType.TEXT_TABLE ->
                                    FormTextTableItem(
                                        sectionId = section.id,
                                        item = item,
                                        editable = editable,
                                        controller = controller,
                                        dragHandle = dragHandle,
                                    )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FormItemDragHandle(modifier: Modifier = Modifier) {
    Box(
        modifier = modifier.size(28.dp),
        contentAlignment = Alignment.Center,
    ) {
        Icon(
            imageVector = Lucide.GripVertical,
            contentDescription = "Arrastar item",
            tint = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.size(18.dp),
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun FormTextItem(
    sectionId: String,
    item: FormItem,
    editable: Boolean,
    controller: FormBuilderController,
    dragHandle: (@Composable () -> Unit)? = null,
) {
    if (!editable) {
        RichBody(html = item.textHtml, modifier = Modifier.fillMaxWidth())
        return
    }
    val layout = appLayoutMetrics()
    val richState = rememberRichTextState()
    val latestHtml = rememberUpdatedState(item.textHtml)
    LaunchedEffect(item.id) {
        if (item.textHtml.isNotBlank()) {
            runCatching { richState.setHtml(item.textHtml) }
        }
    }
    LaunchedEffect(richState) {
        snapshotFlow { richState.annotatedString }.collect {
            val html = richState.toHtml()
            if (html != latestHtml.value) {
                controller.updateTextItem(sectionId, item.id, html)
            }
        }
    }
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(layout.cardPadding),
            verticalArrangement = Arrangement.spacedBy(layout.cardGap),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                dragHandle?.invoke()
                Text(
                    text = "Campo de texto",
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Spacer(modifier = Modifier.weight(1f))
                IconButton(onClick = { controller.removeItem(sectionId, item.id) }) {
                    Icon(
                        imageVector = Lucide.Trash2,
                        contentDescription = "Remover campo de texto",
                        tint = MaterialTheme.colorScheme.error,
                    )
                }
            }
            RichTextToolbar(state = richState)
            Surface(
                shape = AppShapes.medium,
                color = Color.White,
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
            ) {
                RichTextEditor(
                    state = richState,
                    colors = RichTextEditorDefaults.richTextEditorColors(containerColor = Color.White),
                    modifier = Modifier.fillMaxWidth().heightIn(min = 120.dp).padding(AppSpacing.space12),
                )
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun FormQuestionItem(
    sectionId: String,
    item: FormItem,
    editable: Boolean,
    controller: FormBuilderController,
    dragHandle: (@Composable () -> Unit)? = null,
) {
    val question = item.question ?: return
    val typeLabels = FormQuestionType.entries.map { it.label }
    val layout = appLayoutMetrics()
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(layout.cardPadding),
            verticalArrangement = Arrangement.spacedBy(layout.cardGap),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                dragHandle?.invoke()
                Text(
                    text = "Pergunta",
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Spacer(modifier = Modifier.weight(1f))
                if (editable) {
                    IconButton(onClick = { controller.removeItem(sectionId, item.id) }) {
                        Icon(
                            imageVector = Lucide.Trash2,
                            contentDescription = "Remover pergunta",
                            tint = MaterialTheme.colorScheme.error,
                        )
                    }
                }
            }
            AppTextField(
                value = question.title,
                onValueChange = { controller.updateQuestionTitle(sectionId, item.id, it) },
                label = "Enunciado",
                enabled = editable,
                modifier = Modifier.fillMaxWidth(),
            )
            if (editable) {
                Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                    Text(
                        text = "Tipo de resposta",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                    SearchableSelect(
                        leadingIcon = null,
                        options = typeLabels,
                        selected = question.type.label,
                        onSelect = { label ->
                            FormQuestionType.entries.firstOrNull { it.label == label }?.let { type ->
                                controller.updateQuestionType(sectionId, item.id, type)
                            }
                        },
                        modifier = Modifier.fillMaxWidth(),
                        underlined = true,
                        borderColor = MaterialTheme.colorScheme.outline,
                        horizontalPadding = 0.dp,
                    )
                }
                Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                    Text(
                        text = "Obrigatória?",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space4),
                    ) {
                        RadioButton(
                            selected = question.required,
                            onClick = {
                                if (!question.required) controller.toggleRequired(sectionId, item.id)
                            },
                        )
                        Text("Sim")
                        Spacer(modifier = Modifier.width(AppSpacing.space12))
                        RadioButton(
                            selected = !question.required,
                            onClick = {
                                if (question.required) controller.toggleRequired(sectionId, item.id)
                            },
                        )
                        Text("Não")
                    }
                }
            } else {
                Text(
                    text = question.type.label,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }

            when (question.type) {
                FormQuestionType.SINGLE_CHOICE,
                FormQuestionType.MULTIPLE_CHOICE,
                ->
                    ChoiceOptionsEditor(
                        question = question,
                        sectionId = sectionId,
                        itemId = item.id,
                        editable = editable,
                        controller = controller,
                    )
                FormQuestionType.FILE ->
                    FileOptionsEditor(
                        question = question,
                        sectionId = sectionId,
                        itemId = item.id,
                        editable = editable,
                        controller = controller,
                    )
                FormQuestionType.TEXT -> Unit
            }
        }
    }
}

@Composable
private fun ChoiceOptionsEditor(
    question: FormQuestion,
    sectionId: String,
    itemId: String,
    editable: Boolean,
    controller: FormBuilderController,
) {
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
        question.options.forEachIndexed { index, option ->
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                if (question.type == FormQuestionType.MULTIPLE_CHOICE) {
                    Checkbox(checked = false, onCheckedChange = null)
                } else {
                    RadioButton(selected = false, onClick = null)
                }
                AppTextField(
                    value = option.label,
                    onValueChange = { controller.updateOptionLabel(sectionId, itemId, option.id, it) },
                    placeholder = "Opção ${index + 1}",
                    enabled = editable,
                    modifier = Modifier.weight(1f),
                )
                if (editable) {
                    IconButton(onClick = { controller.removeOption(sectionId, itemId, option.id) }) {
                        Icon(
                            imageVector = Lucide.Trash2,
                            contentDescription = "Remover opção",
                            tint = MaterialTheme.colorScheme.error,
                            modifier = Modifier.size(18.dp),
                        )
                    }
                }
            }
        }
        if (editable) {
            AppButton(
                text = "Adicionar opção",
                onClick = { controller.addOption(sectionId, itemId) },
                variant = PortalButtonVariant.Text,
                leadingIcon = Lucide.Plus,
            )
        }
    }
}

@Composable
private fun FileOptionsEditor(
    question: FormQuestion,
    sectionId: String,
    itemId: String,
    editable: Boolean,
    controller: FormBuilderController,
) {
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
        Text(
            text = "Formatos aceitos",
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        FormFileType.entries.forEach { type ->
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                Checkbox(
                    checked = type in question.acceptedFiles,
                    onCheckedChange = {
                        if (editable) {
                            controller.toggleAcceptedFile(sectionId, itemId, type)
                        }
                    },
                    enabled = editable,
                )
                Text(type.label)
            }
        }
    }
}

@Composable
private fun FormChoiceTableItem(
    sectionId: String,
    item: FormItem,
    editable: Boolean,
    showWeights: Boolean,
    controller: FormBuilderController,
    dragHandle: (@Composable () -> Unit)? = null,
) {
    val table = item.choiceTable ?: return
    val layout = appLayoutMetrics()
    val tableLabel = if (showWeights) "Tabela de pontuação" else "Matriz de perguntas"
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(layout.cardPadding),
            verticalArrangement = Arrangement.spacedBy(layout.cardGap),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                dragHandle?.invoke()
                Text(
                    text = tableLabel,
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Spacer(modifier = Modifier.weight(1f))
                if (editable) {
                    IconButton(onClick = { controller.removeItem(sectionId, item.id) }) {
                        Icon(
                            imageVector = Lucide.Trash2,
                            contentDescription = "Remover $tableLabel",
                            tint = MaterialTheme.colorScheme.error,
                        )
                    }
                }
            }
            AppTextField(
                value = table.title,
                onValueChange = { controller.updateChoiceTableTitle(sectionId, item.id, it) },
                label = "Título da tabela",
                enabled = editable,
                modifier = Modifier.fillMaxWidth(),
            )
            Text(
                text = "Colunas de resposta",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            table.columns.forEachIndexed { index, column ->
                key(column.id) {
                    ChoiceColumnRow(
                        sectionId = sectionId,
                        itemId = item.id,
                        column = column,
                        index = index,
                        editable = editable,
                        showWeights = showWeights,
                        controller = controller,
                    )
                }
            }
            if (editable) {
                AppButton(
                    text = "Adicionar coluna",
                    onClick = { controller.addChoiceColumn(sectionId, item.id, showWeights) },
                    variant = PortalButtonVariant.Text,
                    leadingIcon = Lucide.Plus,
                )
            }
            Text(
                text = "Perguntas da tabela",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            if (table.questions.isEmpty()) {
                Text(
                    text = "Adicione perguntas para esta tabela.",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            } else {
                table.questions.forEachIndexed { index, question ->
                    key(question.id) {
                        ChoiceQuestionCard(
                            sectionId = sectionId,
                            itemId = item.id,
                            question = question,
                            index = index,
                            editable = editable,
                            showWeights = showWeights,
                            controller = controller,
                        )
                    }
                }
            }
            if (editable) {
                AppButton(
                    text = "Adicionar pergunta",
                    onClick = { controller.addChoiceQuestion(sectionId, item.id, showWeights) },
                    variant = PortalButtonVariant.Text,
                    leadingIcon = Lucide.Plus,
                )
            }
            if (showWeights) {
                Text(
                    text = "O resultado da tabela é a média ponderada dos pesos das perguntas e das colunas de resposta.",
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
    }
}

@Composable
private fun ChoiceColumnRow(
    sectionId: String,
    itemId: String,
    column: FormChoiceColumn,
    index: Int,
    editable: Boolean,
    showWeights: Boolean,
    controller: FormBuilderController,
) {
    var weightText by remember(column.id) { mutableStateOf(column.weight?.toString().orEmpty()) }
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
    ) {
        RadioButton(selected = false, onClick = null)
        AppTextField(
            value = column.label,
            onValueChange = {
                controller.updateChoiceColumnLabel(sectionId, itemId, column.id, it)
            },
            placeholder = "Resposta ${index + 1}",
            enabled = editable,
            modifier = Modifier.weight(1f),
        )
        if (showWeights) {
            AppTextField(
                value = weightText,
                onValueChange = { value ->
                    weightText = value
                    controller.updateChoiceColumnWeight(sectionId, itemId, column.id, value)
                },
                label = "Peso (0 a 1)",
                enabled = editable,
                modifier = Modifier.width(120.dp),
            )
        }
        if (editable) {
            IconButton(
                onClick = {
                    controller.removeChoiceColumn(sectionId, itemId, column.id)
                },
            ) {
                Icon(
                    imageVector = Lucide.Trash2,
                    contentDescription = "Remover coluna",
                    tint = MaterialTheme.colorScheme.error,
                    modifier = Modifier.size(18.dp),
                )
            }
        }
    }
}

@Composable
private fun ChoiceQuestionCard(
    sectionId: String,
    itemId: String,
    question: FormChoiceQuestion,
    index: Int,
    editable: Boolean,
    showWeights: Boolean,
    controller: FormBuilderController,
) {
    val layout = appLayoutMetrics()
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(layout.cardPadding),
            verticalArrangement = Arrangement.spacedBy(layout.cardGap),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                Text(
                    text = "Pergunta ${index + 1}",
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Spacer(modifier = Modifier.weight(1f))
                if (editable) {
                    IconButton(
                        onClick = {
                            controller.removeChoiceQuestion(sectionId, itemId, question.id)
                        },
                    ) {
                        Icon(
                            imageVector = Lucide.Trash2,
                            contentDescription = "Remover pergunta da tabela",
                            tint = MaterialTheme.colorScheme.error,
                        )
                    }
                }
            }
            AppTextField(
                value = question.title,
                onValueChange = {
                    controller.updateChoiceQuestionTitle(sectionId, itemId, question.id, it)
                },
                label = "Enunciado",
                enabled = editable,
                modifier = Modifier.fillMaxWidth(),
            )
            if (showWeights) {
                AppTextField(
                    value = question.weight?.toString().orEmpty(),
                    onValueChange = {
                        controller.updateChoiceQuestionWeight(sectionId, itemId, question.id, it)
                    },
                    label = "Peso da pergunta",
                    enabled = editable,
                    modifier = Modifier.widthIn(max = 220.dp),
                )
            }
        }
    }
}

@Composable
private fun FormQuestionMatrixItem(
    sectionId: String,
    item: FormItem,
    editable: Boolean,
    controller: FormBuilderController,
    dragHandle: (@Composable () -> Unit)? = null,
) {
    val table = item.questionMatrix ?: return
    val layout = appLayoutMetrics()
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(layout.cardPadding),
            verticalArrangement = Arrangement.spacedBy(layout.cardGap),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                dragHandle?.invoke()
                Text(
                    text = "Matriz de perguntas",
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Spacer(modifier = Modifier.weight(1f))
                if (editable) {
                    IconButton(onClick = { controller.removeItem(sectionId, item.id) }) {
                        Icon(
                            imageVector = Lucide.Trash2,
                            contentDescription = "Remover matriz de perguntas",
                            tint = MaterialTheme.colorScheme.error,
                        )
                    }
                }
            }
            AppTextField(
                value = table.title,
                onValueChange = { controller.updateQuestionMatrixTitle(sectionId, item.id, it) },
                label = "Título da tabela",
                enabled = editable,
                modifier = Modifier.fillMaxWidth(),
            )
            Text(
                text = "Colunas (primeira linha)",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            table.columns.forEachIndexed { index, column ->
                key(column.id) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
                    ) {
                        AppTextField(
                            value = column.label,
                            onValueChange = {
                                controller.updateQuestionMatrixColumnLabel(sectionId, item.id, column.id, it)
                            },
                            placeholder = "Coluna ${index + 1}",
                            enabled = editable,
                            modifier = Modifier.weight(1f),
                        )
                        if (editable) {
                            IconButton(
                                onClick = {
                                    controller.removeQuestionMatrixColumn(sectionId, item.id, column.id)
                                },
                            ) {
                                Icon(
                                    imageVector = Lucide.Trash2,
                                    contentDescription = "Remover coluna",
                                    tint = MaterialTheme.colorScheme.error,
                                    modifier = Modifier.size(18.dp),
                                )
                            }
                        }
                    }
                }
            }
            if (editable) {
                AppButton(
                    text = "Adicionar coluna",
                    onClick = { controller.addQuestionMatrixColumn(sectionId, item.id) },
                    variant = PortalButtonVariant.Text,
                    leadingIcon = Lucide.Plus,
                )
            }
            Text(
                text = "Linhas (primeira coluna)",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            table.rows.forEachIndexed { index, row ->
                key(row.id) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
                    ) {
                        AppTextField(
                            value = row.label,
                            onValueChange = {
                                controller.updateQuestionMatrixRowLabel(sectionId, item.id, row.id, it)
                            },
                            placeholder = "Linha ${index + 1}",
                            enabled = editable,
                            modifier = Modifier.weight(1f),
                        )
                        if (editable) {
                            IconButton(
                                onClick = {
                                    controller.removeQuestionMatrixRow(sectionId, item.id, row.id)
                                },
                            ) {
                                Icon(
                                    imageVector = Lucide.Trash2,
                                    contentDescription = "Remover linha",
                                    tint = MaterialTheme.colorScheme.error,
                                    modifier = Modifier.size(18.dp),
                                )
                            }
                        }
                    }
                }
            }
            if (editable) {
                AppButton(
                    text = "Adicionar linha",
                    onClick = { controller.addQuestionMatrixRow(sectionId, item.id) },
                    variant = PortalButtonVariant.Text,
                    leadingIcon = Lucide.Plus,
                )
            }
            Text(
                text = "Cada interseção entre linha e coluna gera uma pergunta com resposta de texto.",
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

@Composable
private fun FormQuestionTableItem(
    sectionId: String,
    item: FormItem,
    editable: Boolean,
    controller: FormBuilderController,
    dragHandle: (@Composable () -> Unit)? = null,
) {
    val table = item.questionTable ?: return
    val layout = appLayoutMetrics()
    var rowsText by remember(table.id) { mutableStateOf(table.rowCount.toString()) }
    var columnsText by remember(table.id) { mutableStateOf(table.columnCount.toString()) }
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(layout.cardPadding),
            verticalArrangement = Arrangement.spacedBy(layout.cardGap),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                dragHandle?.invoke()
                Text(
                    text = "Tabela de perguntas",
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Spacer(modifier = Modifier.weight(1f))
                if (editable) {
                    IconButton(onClick = { controller.removeItem(sectionId, item.id) }) {
                        Icon(
                            imageVector = Lucide.Trash2,
                            contentDescription = "Remover tabela de perguntas",
                            tint = MaterialTheme.colorScheme.error,
                        )
                    }
                }
            }
            AppTextField(
                value = table.title,
                onValueChange = { controller.updateQuestionTableTitle(sectionId, item.id, it) },
                label = "Título da tabela",
                enabled = editable,
                modifier = Modifier.fillMaxWidth(),
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                AppTextField(
                    value = rowsText,
                    onValueChange = { value ->
                        rowsText = value
                        val rows = value.trim().toIntOrNull()
                        if (rows != null && rows >= 2) {
                            controller.updateQuestionTableDimensions(
                                sectionId = sectionId,
                                itemId = item.id,
                                rows = rows,
                                columns = table.columnCount,
                            )
                        }
                    },
                    label = "Linhas",
                    enabled = editable,
                    modifier = Modifier.widthIn(min = 100.dp, max = 160.dp),
                )
                AppTextField(
                    value = columnsText,
                    onValueChange = { value ->
                        columnsText = value
                        val columns = value.trim().toIntOrNull()
                        if (columns != null && columns > 0) {
                            controller.updateQuestionTableDimensions(
                                sectionId = sectionId,
                                itemId = item.id,
                                rows = table.rowCount,
                                columns = columns,
                            )
                        }
                    },
                    label = "Colunas",
                    enabled = editable,
                    modifier = Modifier.widthIn(min = 100.dp, max = 160.dp),
                )
            }
            Text(
                text = "Cabeçalho (primeira linha)",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            repeat(table.columnCount) { column ->
                AppTextField(
                    value = table.header(column),
                    onValueChange = {
                        controller.updateQuestionTableHeader(sectionId, item.id, column, it)
                    },
                    placeholder = "Coluna ${column + 1}",
                    enabled = editable,
                    modifier = Modifier.fillMaxWidth(),
                )
            }
            Text(
                text = "A primeira linha é o cabeçalho; as demais linhas geram perguntas com resposta de texto.",
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

@Composable
private fun FormTextTableItem(
    sectionId: String,
    item: FormItem,
    editable: Boolean,
    controller: FormBuilderController,
    dragHandle: (@Composable () -> Unit)? = null,
) {
    val table = item.textTable ?: return
    val layout = appLayoutMetrics()
    var rowsText by remember(table.id) { mutableStateOf(table.rowCount.toString()) }
    var columnsText by remember(table.id) { mutableStateOf(table.columnCount.toString()) }
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(layout.cardPadding),
            verticalArrangement = Arrangement.spacedBy(layout.cardGap),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                dragHandle?.invoke()
                Text(
                    text = "Tabela de conteúdo",
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Spacer(modifier = Modifier.weight(1f))
                if (editable) {
                    IconButton(onClick = { controller.removeItem(sectionId, item.id) }) {
                        Icon(
                            imageVector = Lucide.Trash2,
                            contentDescription = "Remover tabela de conteúdo",
                            tint = MaterialTheme.colorScheme.error,
                        )
                    }
                }
            }
            AppTextField(
                value = table.title,
                onValueChange = { controller.updateTextTableTitle(sectionId, item.id, it) },
                label = "Título da tabela",
                enabled = editable,
                modifier = Modifier.fillMaxWidth(),
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                AppTextField(
                    value = rowsText,
                    onValueChange = { value ->
                        rowsText = value
                        val rows = value.trim().toIntOrNull()
                        if (rows != null && rows > 0) {
                            controller.updateTextTableDimensions(
                                sectionId = sectionId,
                                itemId = item.id,
                                rows = rows,
                                columns = table.columnCount,
                            )
                        }
                    },
                    label = "Linhas",
                    enabled = editable,
                    modifier = Modifier.widthIn(min = 100.dp, max = 160.dp),
                )
                AppTextField(
                    value = columnsText,
                    onValueChange = { value ->
                        columnsText = value
                        val columns = value.trim().toIntOrNull()
                        if (columns != null && columns > 0) {
                            controller.updateTextTableDimensions(
                                sectionId = sectionId,
                                itemId = item.id,
                                rows = table.rowCount,
                                columns = columns,
                            )
                        }
                    },
                    label = "Colunas",
                    enabled = editable,
                    modifier = Modifier.widthIn(min = 100.dp, max = 160.dp),
                )
            }
            Column(
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .horizontalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                repeat(table.rowCount) { row ->
                    Row(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
                        repeat(table.columnCount) { column ->
                            AppTextField(
                                value = table.cell(row, column),
                                onValueChange = {
                                    controller.updateTextTableCell(
                                        sectionId = sectionId,
                                        itemId = item.id,
                                        row = row,
                                        column = column,
                                        value = it,
                                    )
                                },
                                placeholder =
                                    if (row == 0) {
                                        "Cabeçalho ${column + 1}"
                                    } else {
                                        "Linha ${row + 1}, coluna ${column + 1}"
                                    },
                                enabled = editable,
                                singleLine = false,
                                modifier = Modifier.width(220.dp),
                            )
                        }
                    }
                }
            }
        }
    }
}
